import { type ParameterType } from './endpoint_metadata'

const DESTROY_MARKER_CLASS = 'parameter-destroy-marker'
const PARAMETER_FIELD_CLASS = 'parameter-field'
const PARAMETER_VALUE_INPUT_CLASS = 'parameter-value-input'

export interface ExistingParameter {
  value: string
  id?: string
  errors?: string[]
}

export function extractRequestIndex (fieldName: string): number {
  const nameMatch = fieldName.match(/\[requests_attributes]\[(\d+)]/)
  return (nameMatch != null) ? parseInt(nameMatch[1], 10) : 0
}

interface ParametersElements {
  card?: HTMLElement
  description: HTMLElement
  fields: HTMLElement
  emptyState: HTMLElement
  parameterTemplate: HTMLTemplateElement
  inputTemplate: HTMLTemplateElement
}

export class ParametersSection {
  private existingFromServer: Record<string, ExistingParameter> = {}

  constructor (
    private readonly el: ParametersElements,
    private readonly requestIndex: number,
    private readonly onValueChanged: () => void
  ) {}

  loadExistingFromServer (params: Array<{ name: string, value: string, id?: string, errors?: string[] }>): void {
    if (!Array.isArray(params)) return

    params.forEach((param) => {
      if ((param.name !== '') && (param.value != null)) {
        this.existingFromServer[param.name] = {
          value: String(param.value),
          id: ((param.id != null) && (param.id !== '')) ? param.id : undefined,
          errors: param.errors
        }
      }
    })
  }

  toggleCard (visible: boolean): void {
    this.toggleVisibility(this.el.card, visible)
  }

  currentValues (): Record<string, string> {
    const values: Record<string, string> = {}

    Object.entries(this.existingValues()).forEach(([name, parameter]) => {
      values[name] = parameter.value
    })

    return values
  }

  update (parameters: ParameterType[], preserveValues: boolean): void {
    const currentValues = preserveValues ? this.existingValues() : {}

    if (!preserveValues) {
      this.addDestroyMarkersForExistingParameters()
    }

    this.clearFields()

    if (parameters.length === 0) {
      this.toggleVisibility(this.el.description, false)
      this.toggleVisibility(this.el.fields, false)
      this.toggleVisibility(this.el.emptyState, true)
      return
    }

    this.showUI()
    this.renderFields(parameters, currentValues, preserveValues)
    this.existingFromServer = {}
  }

  private showUI (): void {
    this.toggleVisibility(this.el.card, true)
    this.toggleVisibility(this.el.description, true)
    this.toggleVisibility(this.el.emptyState, false)
    this.toggleVisibility(this.el.fields, true)
  }

  private renderFields (parameters: ParameterType[], currentValues: Record<string, ExistingParameter>, preserveValues: boolean): void {
    parameters.forEach((param, index) => {
      const existing = currentValues[param.name] ?? (preserveValues ? this.existingFromServer[param.name] : undefined)
      const field = this.createField(param, index, existing?.value, existing?.id, existing?.errors)
      this.el.fields.appendChild(field)
    })
  }

  private clearFields (): void {
    const destroyMarkers = this.el.fields.querySelectorAll(`.${DESTROY_MARKER_CLASS}`)
    const fields = this.el.fields.querySelectorAll(`.${PARAMETER_FIELD_CLASS}`)
    fields.forEach(field => field.remove())

    destroyMarkers.forEach(marker => this.el.fields.appendChild(marker))
  }

  private addDestroyMarkersForExistingParameters (): void {
    const existingParams = this.existingValues()
    Object.entries(existingParams).forEach(([, paramData], index) => {
      if ((paramData.id == null) || (paramData.id === '')) return

      const marker = this.createDestroyMarker(index, paramData.id)
      this.el.fields.appendChild(marker)
    })
  }

  private createDestroyMarker (index: number, id: string): HTMLElement {
    const namePrefix = this.namePrefix(index)
    const container = document.createElement('div')
    container.style.display = 'none'
    container.classList.add(DESTROY_MARKER_CLASS)
    container.innerHTML = `
      <input type="hidden" name="${namePrefix}[id]" value="${id}">
      <input type="hidden" name="${namePrefix}[_destroy]" value="true">
    `
    return container
  }

  private namePrefix (index: number): string {
    return `ccm_test[requests_attributes][${this.requestIndex}][request_parameters_attributes][${index}]`
  }

  private existingValues (): Record<string, ExistingParameter> {
    const values: Record<string, ExistingParameter> = {}
    const existingFields = this.el.fields.querySelectorAll(`.${PARAMETER_FIELD_CLASS}`)

    existingFields.forEach((field) => {
      const paramName = (field as HTMLElement).dataset.parameterName
      if ((paramName == null) || (paramName === '')) return

      const idInput = field.querySelector('input[name*="[id]"]') as HTMLInputElement
      const checkedRadio = field.querySelector<HTMLInputElement>(`.${PARAMETER_VALUE_INPUT_CLASS}[type="radio"]:checked`)
      const textInput = field.querySelector<HTMLInputElement>(`.${PARAMETER_VALUE_INPUT_CLASS}:not([type="radio"])`)

      values[paramName] = {
        value: checkedRadio?.value ?? textInput?.value ?? '',
        id: idInput?.value
      }
    })

    return values
  }

  private createField (param: ParameterType, index: number, existingValue?: string, existingId?: string, errors?: string[]): HTMLElement {
    const template = this.el.parameterTemplate.content.cloneNode(true) as DocumentFragment
    const fieldDiv = template.querySelector(`.${PARAMETER_FIELD_CLASS}`) as HTMLElement

    fieldDiv.dataset.parameterName = param.name

    this.setFieldLabel(fieldDiv, param)
    this.setFieldDescription(fieldDiv, param)
    this.setFieldInputs(fieldDiv, param, index, existingValue, existingId)
    this.setFieldErrors(fieldDiv, errors)
    this.attachValueChangeListeners(fieldDiv)
    this.attachClearButton(fieldDiv, param.type)

    return fieldDiv
  }

  private setFieldLabel (fieldDiv: HTMLElement, param: ParameterType): void {
    const label = fieldDiv.querySelector('.parameter-label') as HTMLElement
    label.textContent = param.name

    if (param.required) {
      const requiredSpan = fieldDiv.querySelector('.parameter-required') as HTMLElement
      requiredSpan.style.display = 'inline'
    }
  }

  private setFieldDescription (fieldDiv: HTMLElement, param: ParameterType): void {
    const desc = fieldDiv.querySelector('.parameter-description') as HTMLElement
    const description = param.in === 'path'
      ? `Will replace {${param.name}} in the endpoint URL`
      : param.description

    if ((description != null) && (description !== '')) {
      desc.textContent = description
      desc.style.display = 'block'
    }
  }

  private setFieldInputs (fieldDiv: HTMLElement, param: ParameterType, index: number, existingValue?: string, existingId?: string): void {
    const namePrefix = this.namePrefix(index)

    this.setHiddenField(fieldDiv, '.parameter-name-input', `${namePrefix}[name]`, param.name)
    this.setHiddenField(fieldDiv, '.parameter-destroy-input', `${namePrefix}[_destroy]`, '')

    if ((existingId != null) && (existingId !== '')) {
      this.addIdField(fieldDiv, namePrefix, existingId)
    }

    const inputContainer = fieldDiv.querySelector('.parameter-input-container') as HTMLElement
    const input = this.createInputByType(param, namePrefix, existingValue)
    inputContainer.appendChild(input)
  }

  private setHiddenField (container: HTMLElement, selector: string, name: string, value: string): void {
    const input = container.querySelector(selector) as HTMLInputElement
    input.name = name
    if (value !== '') input.value = value
  }

  private addIdField (fieldDiv: HTMLElement, namePrefix: string, existingId: string): void {
    const idInput = document.createElement('input')
    idInput.type = 'hidden'
    idInput.name = `${namePrefix}[id]`
    idInput.value = existingId
    fieldDiv.insertBefore(idInput, fieldDiv.firstChild)
  }

  private setFieldErrors (fieldDiv: HTMLElement, errors?: string[]): void {
    const errorElement = fieldDiv.querySelector('.parameter-error') as HTMLElement
    if ((errors != null) && errors.length > 0) {
      errorElement.textContent = errors.join(', ')
      errorElement.style.display = 'block'

      const input = fieldDiv.querySelector(`.${PARAMETER_VALUE_INPUT_CLASS}`) as HTMLInputElement
      if (input != null) {
        input.classList.add('ring-red-500', 'focus:ring-red-500')
      }
    }
  }

  private attachValueChangeListeners (fieldDiv: HTMLElement): void {
    fieldDiv.addEventListener('input', this.onValueChanged)
    fieldDiv.addEventListener('change', this.onValueChanged)
  }

  private attachClearButton (fieldDiv: HTMLElement, paramType: string): void {
    const clearBtn = fieldDiv.querySelector('.parameter-clear-btn') as HTMLButtonElement
    clearBtn?.addEventListener('click', () => {
      if (paramType === 'boolean') {
        this.clearBooleanInput(fieldDiv)
      } else {
        this.clearTextInput(fieldDiv)
      }

      this.onValueChanged()
    })
  }

  private clearBooleanInput (fieldDiv: HTMLElement): void {
    fieldDiv.querySelectorAll<HTMLInputElement>(`.${PARAMETER_VALUE_INPUT_CLASS}[type="radio"]`).forEach(r => { r.checked = false })
  }

  private clearTextInput (fieldDiv: HTMLElement): void {
    const input = fieldDiv.querySelector<HTMLInputElement>(`.${PARAMETER_VALUE_INPUT_CLASS}`)
    if (input != null) input.value = ''
  }

  private createInputByType (param: ParameterType, namePrefix: string, existingValue?: string): HTMLElement {
    const inputFragment = this.el.inputTemplate.content.cloneNode(true) as DocumentFragment
    const inputName = `${namePrefix}[value]`
    const wrapper = document.createElement('div')

    if (param.type === 'boolean') {
      this.setupBooleanInput(inputFragment, inputName, param, existingValue)
    } else {
      this.setupTextOrIntegerInput(inputFragment, inputName, param, existingValue)
    }

    Array.from(inputFragment.childNodes)
      .filter(n => n.nodeType === Node.ELEMENT_NODE)
      .forEach(n => wrapper.appendChild(n))

    return wrapper
  }

  private setupBooleanInput (fragment: DocumentFragment, inputName: string, param: ParameterType, existingValue?: string): void {
    fragment.querySelector(`.${PARAMETER_VALUE_INPUT_CLASS}:not([type="radio"])`)?.remove()
    fragment.querySelectorAll<HTMLInputElement>(`.${PARAMETER_VALUE_INPUT_CLASS}[type="radio"]`).forEach(radio => {
      radio.name = inputName
      radio.required = param.required
      radio.checked = existingValue === radio.value
    })
  }

  private setupTextOrIntegerInput (fragment: DocumentFragment, inputName: string, param: ParameterType, existingValue?: string): void {
    fragment.querySelector('.flex.items-center.space-x-4')?.remove()
    const input = fragment.querySelector(`.${PARAMETER_VALUE_INPUT_CLASS}`) as HTMLInputElement
    input.name = inputName
    input.type = param.type === 'integer' ? 'number' : 'text'
    input.required = param.required

    if (param.type === 'integer') {
      input.step = '1'
    } else {
      if ((param.pattern != null) && (param.pattern !== '')) input.pattern = param.pattern
      input.placeholder = this.capitalizeFirst(param.name) + ' placeholder'
    }

    input.value = existingValue ?? ((param.default != null) ? String(param.default) : '')
  }

  private capitalizeFirst (str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  private toggleVisibility (element: HTMLElement | undefined, visible: boolean): void {
    if (element == null) return
    element.style.display = visible ? 'block' : 'none'
  }
}
